import { weekFileName } from '../../../lib/weekFileName';
import { ListTotalsBar, sumByCurrency } from '../../ui/list-totals-bar';
import { useTranslation } from 'react-i18next';
import { WeekFilter } from '../../ui/week-filter';
import { matchesWeek } from '../../../lib/weekFilter';
import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { SortableHeader, useSort, ResetViewButton } from '../../ui/SortableHeader';
import { RefreshCw, FileText, Eye, Trash2, Archive, CheckSquare, Filter, X } from 'lucide-react';
import { fmtAmt, fmtDate } from './helpers';

const API = process.env.REACT_APP_BACKEND_URL;

export const RecordsTab = ({ isAdmin }) => {
  const { t } = useTranslation();
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [directionFilter, setDirectionFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [sellerNipFilter, setSellerNipFilter] = useState('');
  const [buyerNipFilter, setBuyerNipFilter] = useState('');
  const [paidFilter, setPaidFilter] = useState('');
  const [envFilter, setEnvFilter] = useState('');
  const [weekFilter, setWeekFilter] = useState(null);
  const visibleRecords = records.filter((r) => matchesWeek(weekFilter, r.issue_date || r.downloaded_at));
  const [selectedRecords, setSelectedRecords] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [xmlDialogOpen, setXmlDialogOpen] = useState(false);
  const [viewingXml, setViewingXml] = useState('');
  const [sortKey, sortDir, toggleSort, comparator, resetSort] = useSort('date', 'desc', 'ksef-records');
  const isViewCustomized = sortKey !== 'date' || sortDir !== 'desc';

  const hasFilters = directionFilter || monthFilter || sellerNipFilter || buyerNipFilter || paidFilter || envFilter;

  const fetchRecords = useCallback(async () => {
    setRecordsLoading(true);
    try {
      const params = new URLSearchParams({ limit: 200 });
      if (directionFilter) params.append('direction', directionFilter);
      if (monthFilter) params.append('month', monthFilter);
      if (sellerNipFilter) params.append('seller_nip', sellerNipFilter);
      if (buyerNipFilter) params.append('buyer_nip', buyerNipFilter);
      if (paidFilter !== '') params.append('paid', paidFilter === 'true');
      if (envFilter) params.append('environment', envFilter);
      const r = await axios.get(`${API}/api/ksef/invoices?${params}`);
      setRecords(r.data.records || []);
    } catch { toast.error(t('auto.misc2.failedToLoadRecords')); }
    finally { setRecordsLoading(false); }
  }, [directionFilter, monthFilter, sellerNipFilter, buyerNipFilter, paidFilter, envFilter]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const toggleRecord = (id) => {
    setSelectedRecords(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const toggleAllRecords = () => {
    setSelectedRecords(prev => prev.size === records.length ? new Set() : new Set(records.map(r => r.id)));
  };

  const buildZipHint = () => {
    const parts = [];
    if (monthFilter) parts.push(monthFilter);
    if (sellerNipFilter) parts.push(`seller-${sellerNipFilter}`);
    if (buyerNipFilter) parts.push(`buyer-${buyerNipFilter}`);
    if (paidFilter === 'true') parts.push('paid');
    if (paidFilter === 'false') parts.push('unpaid');
    if (directionFilter) parts.push(directionFilter);
    return parts.length ? parts.join('_') : 'all';
  };

  const clearFilters = () => {
    setDirectionFilter(''); setMonthFilter(''); setSellerNipFilter('');
    setBuyerNipFilter(''); setPaidFilter(''); setEnvFilter('');
  };

  const downloadBlob = async (url, filename, contentType) => {
    try {
      const r = await axios.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([r.data], { type: contentType }));
      const a = document.createElement('a'); a.href = blobUrl; a.download = weekFileName(filename); a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (e) { toast.error(`Download failed: ${e.response?.data?.detail || e.message}`); }
  };

  const bulkDownload = async (endpoint, zipSuffix, errorMsg) => {
    if (!selectedRecords.size) return;
    setBulkLoading(true);
    try {
      const hint = buildZipHint();
      const r = await axios.post(`${API}${endpoint}`, { ids: [...selectedRecords], filename_hint: hint }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/zip' }));
      const a = document.createElement('a'); a.href = url;
      a.download = weekFileName(`ksef_${hint}${zipSuffix}.zip`); a.click();
      URL.revokeObjectURL(url);
      toast.success(t('auto.misc2.zipDownloaded'));
    } catch { toast.error(errorMsg); }
    finally { setBulkLoading(false); }
  };

  const handleBulkDelete = async () => {
    if (!selectedRecords.size) return;
    if (!window.confirm(`Delete ${selectedRecords.size} records? This cannot be undone.`)) return;
    setBulkLoading(true);
    try {
      await axios.post(`${API}/api/ksef/invoices/bulk-delete`, { ids: [...selectedRecords] });
      toast.success(`Deleted ${selectedRecords.size} records`);
      setSelectedRecords(new Set()); fetchRecords();
    } catch { toast.error(t('auto.misc2.bulkDeleteFailed')); }
    finally { setBulkLoading(false); }
  };

  const handleViewXml = async (ref) => {
    try {
      const r = await axios.get(`${API}/api/ksef/invoices/${ref}`, { params: { by_ref: true } }).catch(() =>
        axios.get(`${API}/api/ksef/download/${ref}/xml`, { responseType: 'text' })
      );
      setViewingXml(typeof r.data === 'string' ? r.data : (r.data.invoice_xml || '— No XML stored for this record —\n\nLikely cause: this record predates the fix that persists XML on failed submissions, or was never converted to FA(3) before the error. Try re-submitting the underlying invoice and the new attempt will save its XML automatically.'));
      setXmlDialogOpen(true);
    } catch { toast.error(t('auto.misc2.couldNotLoadXml')); }
  };

  const handleDeleteRecord = async (id) => {
    if (!window.confirm(t('auto.misc2.deleteThisRecord'))) return;
    try {
      await axios.delete(`${API}/api/ksef/invoices/${id}`);
      toast.success(t('auto.misc2.recordDeleted')); fetchRecords();
    } catch { toast.error(t('auto.misc2.deleteFailed')); }
  };

  const buildRecordFilename = (r, ext) => {
    const date = (r.issue_date || r.downloaded_at || '').slice(0, 10);
    const seller = (r.seller_name || '').replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').slice(0, 20).replace(/_$/, '');
    const invNr = (r.invoice_number || r.id || '').replace(/[^a-zA-Z0-9]/g, '_');
    return 'ksef_' + [date, seller, invNr].filter(Boolean).join('_') + '.' + ext;
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm">{t('auto.misc2.ksefInvoiceRecords')}</CardTitle>
            <Button size="sm" variant="ghost" onClick={fetchRecords} disabled={recordsLoading} data-testid="btn-fetchrecords">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${recordsLoading ? 'animate-spin' : ''}`} />Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Filters */}
          <div className="flex flex-wrap gap-2 items-end p-2 bg-gray-50 rounded border">
            <Filter className="h-3.5 w-3.5 text-gray-400 self-center" />
            <div>
              <Label className="text-xs">{t('auto.misc2.month')}</Label>
              <Input type="month" value={monthFilter} onChange={e => setMonthFilter(e.target.value)} className="h-8 w-36" data-testid="month-filter" />
            </div>
            <div>
              <Label className="text-xs">{t('weekFilter.week')}</Label>
              <div><WeekFilter value={weekFilter} onChange={setWeekFilter} testId="ksef-week-filter" /></div>
            </div>
            <div>
              <Label className="text-xs">{t('auto.misc2.sellerNip')}</Label>
              <Input placeholder={t('auto.misc2.nip')} value={sellerNipFilter} onChange={e => setSellerNipFilter(e.target.value)} className="h-8 w-32" data-testid="seller-nip-filter" />
            </div>
            <div>
              <Label className="text-xs">{t('auto.misc2.buyerNip')}</Label>
              <Input placeholder={t('auto.misc2.nip')} value={buyerNipFilter} onChange={e => setBuyerNipFilter(e.target.value)} className="h-8 w-32" data-testid="buyer-nip-filter" />
            </div>
            <div>
              <Label className="text-xs">{t('auto.misc2.direction')}</Label>
              <Select value={directionFilter || 'all'} onValueChange={v => setDirectionFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-8 w-32"><SelectValue placeholder={t('auto.misc2.all')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('auto.misc2.all')}</SelectItem>
                  <SelectItem value="incoming">{t('auto.misc2.incoming')}</SelectItem>
                  <SelectItem value="outgoing">{t('auto.misc2.outgoing')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('auto.misc2.environment')}</Label>
              <Select value={envFilter || 'all'} onValueChange={v => setEnvFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-8 w-32" data-testid="env-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('auto.misc2.allEnvs')}</SelectItem>
                  <SelectItem value="live">{t('auto.misc2.liveOnly')}</SelectItem>
                  <SelectItem value="prod">{t('auto.misc2.production2')}</SelectItem>
                  <SelectItem value="demo">{t('auto.misc2.demo2')}</SelectItem>
                  <SelectItem value="test">{t('auto.misc2.test2')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('auto.misc2.payment')}</Label>
              <Select value={paidFilter || 'all'} onValueChange={v => setPaidFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-8 w-28"><SelectValue placeholder={t('auto.misc2.all')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('auto.misc2.all')}</SelectItem>
                  <SelectItem value="true">{t('auto.misc2.paid')}</SelectItem>
                  <SelectItem value="false">{t('auto.misc2.unpaid')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {hasFilters && (
              <Button size="sm" variant="ghost" onClick={clearFilters} className="self-end h-8" data-testid="btn-clearfilters">
                <X className="h-3.5 w-3.5 mr-1" />{t('auto.misc2.clear')}
              </Button>
            )}
            {isViewCustomized && (
              <div className="self-end h-8 flex items-center">
                <ResetViewButton show onReset={resetSort} testId="reset-view-ksef-records" />
              </div>
            )}
          </div>

          {/* Bulk toolbar */}
          {selectedRecords.size > 0 && (
            <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded" data-testid="bulk-toolbar">
              <CheckSquare className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-700">{selectedRecords.size} selected</span>
              {isAdmin && (
                <Button size="sm" variant="destructive" onClick={handleBulkDelete} disabled={bulkLoading} data-testid="bulk-delete-btn">
                  <Trash2 className="h-3.5 w-3.5 mr-1" />{t('auto.misc2.delete')}
                </Button>
              )}
              <Button size="sm" onClick={() => bulkDownload('/api/ksef/invoices/bulk-xml', '_xml', 'Bulk XML download failed')} disabled={bulkLoading} data-testid="bulk-xml-btn">
                <FileText className="h-3.5 w-3.5 mr-1" />{bulkLoading ? t('auto.misc2.buildingZip') : t('auto.misc2.downloadXmlsZip')}
              </Button>
              <Button size="sm" onClick={() => bulkDownload('/api/ksef/invoices/bulk-pdf', '', 'Bulk PDF failed — ensure all selected invoices have XML downloaded')} disabled={bulkLoading} data-testid="bulk-pdf-btn">
                <Archive className="h-3.5 w-3.5 mr-1" />{bulkLoading ? t('auto.misc2.buildingZip') : t('auto.misc2.downloadPdfsZip')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedRecords(new Set())}>
                <X className="h-3.5 w-3.5" />
              </Button>
              <span className="text-xs text-gray-500">ZIP: ksef_{buildZipHint()}.zip</span>
            </div>
          )}

          <ListTotalsBar
            count={visibleRecords.length}
            week={weekFilter}
            groups={[
              { key: 'net', label: t('totals.net'), values: sumByCurrency(visibleRecords, (r) => r.currency, (r) => r.net_amount) },
              { key: 'gross', label: t('totals.gross'), values: sumByCurrency(visibleRecords, (r) => r.currency, (r) => r.gross_amount) },
            ]}
            testId="ksef-totals"
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <Checkbox
                    checked={records.length > 0 && selectedRecords.size === records.length}
                    onCheckedChange={toggleAllRecords}
                    data-testid="select-all-records"
                  />
                </TableHead>
                <SortableHeader column="direction" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('auto.misc2.dir')}</SortableHeader>
                <SortableHeader column="environment" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('auto.misc2.env')}</SortableHeader>
                <SortableHeader column="ksef_reference_number" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('auto.misc2.ksefReference')}</SortableHeader>
                <SortableHeader column="invoice_number" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('auto.misc2.invoice')}</SortableHeader>
                <SortableHeader column="seller_nip" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('auto.misc2.sellerNip')}</SortableHeader>
                <SortableHeader column="buyer_nip" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('auto.misc2.buyerNip')}</SortableHeader>
                <SortableHeader column="amount" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('auto.misc2.amount')}</SortableHeader>
                <SortableHeader column="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('auto.misc2.status')}</SortableHeader>
                <SortableHeader column="date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort}>{t('auto.misc2.date')}</SortableHeader>
                <TableHead>{t('auto.misc2.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.length === 0
                ? <TableRow><TableCell colSpan={11} className="text-center text-gray-400 py-8">
                    {hasFilters ? t('auto.misc2.noRecordsMatchCurrentFilters') : t('auto.misc2.noRecordsYet')}
                  </TableCell></TableRow>
                : visibleRecords.sort(comparator((r, key) => {
                    switch (key) {
                      case 'direction': return r.direction || '';
                      case 'environment': return r.environment || '';
                      case 'ksef_reference_number': return r.ksef_reference_number || '';
                      case 'invoice_number': return r.invoice_number || '';
                      case 'seller_nip': return r.seller_nip || '';
                      case 'buyer_nip': return r.buyer_nip || '';
                      case 'amount': return r.amount ?? r.total_gross ?? 0;
                      case 'status': return r.status || '';
                      case 'date': return r.issue_date ? new Date(r.issue_date).getTime() : (r.created_at ? new Date(r.created_at).getTime() : 0);
                      default: return '';
                    }
                  })).map(r => (
                    <TableRow key={r.id} className={selectedRecords.has(r.id) ? 'bg-blue-50' : ''}>
                      <TableCell>
                        <Checkbox checked={selectedRecords.has(r.id)} onCheckedChange={() => toggleRecord(r.id)} />
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs font-medium ${r.direction === 'incoming' ? 'text-blue-600' : 'text-green-600'}`}>
                          {r.direction === 'incoming' ? t('auto.misc2.in') : t('auto.misc2.out')}
                        </span>
                      </TableCell>
                      <TableCell>
                        {r.environment === 'prod' ? (
                          <Badge variant="outline" className="text-[10px] font-medium border-green-300 bg-green-50 text-green-700">{t('auto.misc2.prod')}</Badge>
                        ) : r.environment === 'demo' ? (
                          <Badge variant="outline" className="text-[10px] font-medium border-amber-300 bg-amber-50 text-amber-700">{t('auto.misc2.demo')}</Badge>
                        ) : r.environment === 'test' ? (
                          <Badge variant="outline" className="text-[10px] font-medium border-blue-300 bg-blue-50 text-blue-700">{t('auto.misc2.test')}</Badge>
                        ) : (
                          <span className="text-[10px] text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs max-w-[140px] truncate">{r.ksef_reference_number}</TableCell>
                      <TableCell className="text-xs">{r.invoice_number || '—'}</TableCell>
                      <TableCell className="font-mono text-xs">{r.seller_nip || '—'}</TableCell>
                      <TableCell className="font-mono text-xs">{r.buyer_nip || '—'}</TableCell>
                      <TableCell className="text-xs">{fmtAmt(r.gross_amount, r.currency)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{r.status}</Badge>
                        {r.paid != null && (
                          <span className={`ml-1 text-[10px] font-medium ${r.paid ? 'text-green-600' : 'text-amber-600'}`}>
                            {r.paid ? t('auto.misc2.paid2') : t('auto.misc2.unpaid2')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{fmtDate(r.issue_date || r.downloaded_at)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {(() => {
                            // For freshly-submitted records that don't yet have a
                            // KSeF number assigned, fall back to the record id so
                            // the download endpoints can still resolve it.
                            const refForDownload = r.ksef_reference_number || r.id;
                            const hasXml = !!refForDownload;
                            return (
                              <>
                                <Button size="icon" variant="ghost" className="h-7 w-7" title={t('auto.misc2.viewXml')}
                                  onClick={() => handleViewXml(r.id)} data-testid="view-xml-btn">
                                  <Eye className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7" title={t('auto.misc2.downloadXml')}
                                  disabled={!hasXml}
                                  onClick={() => downloadBlob(
                                    `${API}/api/ksef/download/${refForDownload}/xml`,
                                    buildRecordFilename(r, 'xml'), 'application/xml'
                                  )}
                                  data-testid="download-xml-btn">
                                  <FileText className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" title={t('auto.misc2.downloadPdf')}
                                  disabled={!hasXml}
                                  onClick={() => downloadBlob(
                                    `${API}/api/ksef/download/${refForDownload}/pdf`,
                                    buildRecordFilename(r, 'pdf'), 'application/pdf'
                                  )}
                                  data-testid="download-pdf-btn">
                                  <FileText className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            );
                          })()}
                          {isAdmin && (
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-600" title={t('auto.misc2.delete')}
                              onClick={() => handleDeleteRecord(r.id)} data-testid="delete-record-btn">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
              }
            </TableBody>
          </Table>
          {records.length > 0 && (
            <div className="text-xs text-gray-400 text-right">{records.length} record(s){hasFilters ? ' (filtered)' : ''}</div>
          )}
        </CardContent>
      </Card>

      {/* XML viewer dialog */}
      <Dialog open={xmlDialogOpen} onOpenChange={setXmlDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{t('auto.misc2.invoiceXmlFa3')}</DialogTitle>
            <DialogDescription>{t('auto.misc2.rawKsefInvoiceXmlContentFor')}</DialogDescription>
          </DialogHeader>
          <div className="overflow-auto max-h-[55vh]">
            <pre className="text-xs bg-gray-50 p-3 rounded border font-mono whitespace-pre-wrap">{viewingXml}</pre>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setXmlDialogOpen(false)} data-testid="dialog-close-btn">{t('auto.misc2.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
